import { startTransition, useOptimistic } from 'react';
import { useSearchParams } from 'react-router';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import { parsePageQuery, parsePerPageQuery } from '@/utils';
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from '@/components/ui/pagination';
import { DataTablePerPage } from './data-table-per-page';

interface Props {
  total: number;
}

export function DataTablePagination({ total }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [optimisticValue, setOptimisticValue] = useOptimistic(
    parsePageQuery(searchParams.get('page')),
  );
  const perPage = parsePerPageQuery(searchParams.get('per_page'));
  const lastPage = Math.ceil(total / perPage);
  const isFirstPage = optimisticValue === 1;
  const isLastPage = optimisticValue === lastPage || total === 0;
  const rowsCount = total.toLocaleString();

  function goToPage(value: number) {
    startTransition(() => {
      setOptimisticValue(value);
      setSearchParams((prev) => {
        prev.set('page', value.toString());

        return prev;
      });
    });
  }

  return (
    <>
      {/* Desktop version */}
      <Pagination className="hidden sm:flex sm:items-center sm:justify-between sm:px-2">
        <div className="text-muted-foreground text-sm">{rowsCount} rows</div>
        <div className="flex items-center gap-10">
          <DataTablePerPage />
          <p className="text-sm font-medium">
            Page {optimisticValue.toLocaleString()} of{' '}
            {lastPage.toLocaleString()}
          </p>
          <PaginationContent className="gap-2">
            <PaginationItem>
              <Button
                disabled={isFirstPage}
                onClick={() => goToPage(1)}
                size="icon"
                variant="outline"
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft className="size-4" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button
                disabled={isFirstPage}
                onClick={() => goToPage(optimisticValue - 1)}
                size="icon"
                variant="outline"
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="size-4" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button
                disabled={isLastPage}
                onClick={() => goToPage(optimisticValue + 1)}
                size="icon"
                variant="outline"
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="size-4" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button
                disabled={isLastPage}
                onClick={() => goToPage(lastPage)}
                size="icon"
                variant="outline"
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight className="size-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </div>
      </Pagination>
      {/* Mobile version */}
      <Pagination className="flex items-center justify-between gap-4 px-2 sm:hidden sm:px-0">
        <div className="text-muted-foreground text-sm">{rowsCount} rows</div>
        <div className="flex items-center gap-4">
          <PaginationContent className="gap-2">
            <PaginationItem>
              <Button
                disabled={isFirstPage}
                onClick={() => goToPage(optimisticValue - 1)}
                size="icon"
                variant="outline"
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="size-4" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button
                disabled={isLastPage}
                onClick={() => goToPage(optimisticValue + 1)}
                size="icon"
                variant="outline"
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="size-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
          <p className="text-sm font-medium">
            Page {optimisticValue.toLocaleString()} of{' '}
            {lastPage.toLocaleString()}
          </p>
        </div>
      </Pagination>
    </>
  );
}
